import i18next from 'i18next';
import { Suspense } from 'react';
import { QueryClientProvider } from 'react-query';
import DefaultQueryClientProvider, { queryClient } from './queryClient';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Router } from 'react-router-dom';
import { Api, getImpersonationEndedReason, handleImpersonationEnded, isCompanyDisabledResponse } from 'src/api';
import Path, { API_PREFIX } from 'src/helpers/Path';
import history from 'src/history';
import { createRoot } from 'react-dom/client';
import { defaultLanguage, supportedLanguages } from 'src/i18n/languages';
import { Layout, Public, SignUp, Login, CompanyDisabled } from 'src/pages';
import topRoutesList from 'src/routing/routes/topRoutesList';
import { redirectToLogin, redirectToSignup, redirectToSuccessCreateCompany } from 'src/routing/RoutingUtils';
import './styles/index.js';
import { setWith, merge } from 'lodash-es';
import { tryRecoverFromStaleDeps } from 'src/helpers/recoverFromStaleDeps';
import { keyAccessPostProcessor, recordMissingKey } from 'src/i18n/keyAccessLog';
import { initializePublicDataDog } from 'src/helpers/DataDog';

window.osloVersion = NPM_PACKAGE_VERSION;

if (import.meta.env.DEV) {
  // Dev-only: auto-recover from a stale Vite optimize-deps cache by recollecting + reloading.
  // Covers lazy components that aren't wrapped in a route-level ErrorBoundary.
  window.addEventListener('unhandledrejection', (e) => {
    tryRecoverFromStaleDeps(e.reason);
  });
  window.addEventListener('error', (e) => {
    tryRecoverFromStaleDeps(e.error);
  });
}

window.addEventListener('vite:preloadError', event => {
  console.error('Wasn\'t able to load lazy chunk. Most likely the version is obsolete. Reloading', event);
  setTimeout(() => document.location.reload(), 100);
});

const redirectRules = {
  ['/public/links/registerExpense']: '/myself/claims'
};

// Entry points that render without a session, so their monitoring config cannot come from the
// company-scoped bootstrap.
const UNAUTHENTICATED_ENTRIES = ['public', 'login', 'resetpassword', 'selfsignup'];

const url = new URL(API_PREFIX, document.location);

const makeRequest = (path, params = {}) => (
  fetch(Path.join(url.pathname, Path.applyParams(path.toString(), params)))
    .then(r => new Promise((resolve, reject) => r.ok && r.json ? resolve(r.json()) : reject(r)))
);

const initI18next = async (locale, publicI18n) => {
  const localesToLoad = [defaultLanguage];
  if (locale !== defaultLanguage) {
    localesToLoad.push(locale);
  }
  let translations;
  if (publicI18n) {
    translations = await Promise.all(localesToLoad.map(locale => import(`./i18n/locales/public_${supportedLanguages[locale]}.json`)));
  } else {
    translations = await Promise.all(localesToLoad.map(locale => import(`./i18n/locales/client_${supportedLanguages[locale]}.json`)));
  }

  const resources = localesToLoad.reduce((memo, locale, i) => {
    memo[locale] = { translation: translations[i].default };
    return memo;
  }, {});

  const printMissingKeys = (() => {

    let collectedKeys = {};
    let debounceTimer;

    const printCollectedKeys = () => {
      console.log(JSON.stringify(collectedKeys, null, 2));
      collectedKeys = {};
    };

    return (key) => {
      // setWith(..., Object) forces plain objects for every path segment, so numeric key
      // parts (e.g. ICD codes like "9957") are kept as string keys instead of being treated
      // as array indices — which otherwise produces huge sparse-array logs.
      const nestedObject = setWith({}, key, '', Object);
      collectedKeys = merge(collectedKeys, nestedObject);

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(printCollectedKeys, 3000);
    };
  })();


  return i18next.use(keyAccessPostProcessor).init({
    compatibilityJSON: 'v3',
    fallbackLng: defaultLanguage,
    initImmediate: false,
    returnEmptyString: false,
    lng: locale,
    debug: false,
    resources,
    // Recording is an in-memory Set with no I/O, so it runs everywhere — gating it on
    // CONFIG.debug would leave the signal unavailable on stage/prod, where MOMENT_DEBUG
    // is false and where the reports actually come from. Only the console dump is gated.
    saveMissing: true,
    postProcess: ['recordKeyAccess'],
    missingKeyHandler: (_ls, _n, key) => {
      recordMissingKey(key);
      if (CONFIG.debug) {
        printMissingKeys(key);
      }
    },
    interpolation: {
      escapeValue: false,
      prefix: '{',
      suffix: '}'
    }
  });
};

// Standalone, like login: the company just refused the bootstrap request, so there is no Layout or Identity to build on.
const renderCompanyDisabled = async (companyCode) => {
  await initI18next(defaultLanguage, true);
  root.render((
    <Router history={history}>
      <Suspense fallback={null}>
        <DefaultQueryClientProvider>
          <CompanyDisabled companyCode={companyCode} />
        </DefaultQueryClientProvider>
      </Suspense>
    </Router>
  ));
  const loading = document.getElementById('loading');
  loading.parentNode.removeChild(loading);
};

const catchRequestError = (ns, companyCode) => async (e) => {
  const impersonationEndedReason = getImpersonationEndedReason(e);
  if (impersonationEndedReason) {
    handleImpersonationEnded(impersonationEndedReason);
    return;
  }
  // Before the 401/403 branch: a logged-in user sent to login is sent straight back into the same company.
  if (isCompanyDisabledResponse(e)) {
    await renderCompanyDisabled(companyCode);
    throw new Error('companyDisabled');
  }
  if ([401, 403].includes(e.status)) {
    if (ns === 'users/me') {
      const initialOptions = await makeRequest(Api.signup.initialOptions).catch(err => console.log(err));
      const noCompanyUser = initialOptions?.roles?.some(item => item === 'ROLE_USER_WITHOUT_A_COMPANY') || false;
      if (noCompanyUser) {
        redirectToSignup();
        return;
      }
      if (initialOptions?.roles?.some(item => item === 'ROLE_USER_WITHOUT_VERIFICATION')) {
        redirectToSuccessCreateCompany();
        return;
      }
    }
    redirectToLogin('/');
    return;
  }
  document.querySelector('#loading').classList.add('show-initial-error');
  e.ns = ns;
  throw e;
};

const redirect = (pathname) => {
  const url = new URL(window.location);
  url.pathname = pathname;
  window.history.replaceState(null, null, url);
};

const container = document.querySelector('#app');
const root = createRoot(container);

(async () => {
  const redirectToNewPathname = Object.keys(redirectRules).find(originalPathname => window.location.pathname === originalPathname);
  if (redirectToNewPathname) {
    redirect(redirectRules[redirectToNewPathname]);
  }

  const pathnameParts = window.location.pathname.split('/');
  let companyCode = pathnameParts[1];

  if (UNAUTHENTICATED_ENTRIES.includes(companyCode)) {
    // must be without await — monitoring must never delay or break the render
    initializePublicDataDog(() => makeRequest(Api.public.initialOptions))
      .catch(err => console.error('Could not start browser monitoring', err));
  }

  if (companyCode === 'public') {
    await initI18next(defaultLanguage, true);

    root.render((
      <Router history={history}>
        <Suspense fallback={null}>
          <DefaultQueryClientProvider>
            <Public />
          </DefaultQueryClientProvider>
        </Suspense>
      </Router>
    ));
    const loading = document.getElementById('loading');
    loading.parentNode.removeChild(loading);
    throw new Error('public');
  }

  if (companyCode === 'login' || companyCode === 'resetpassword') {
    const systemUser = await makeRequest(Api.users.me).catch(console.error);

    // only redirect away from login page if user is logged in
    if (systemUser && companyCode === 'login') {
      window.location.href = '/';
      return;
    }

    await initI18next(defaultLanguage, true);
    root.render((
      <Router history={history}>
        <Suspense fallback={null}>
          <DefaultQueryClientProvider>
            <Login />
            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          </DefaultQueryClientProvider>
        </Suspense>
      </Router>
    ));
    const loading = document.getElementById('loading');
    loading.parentNode.removeChild(loading);
    throw new Error('login');
  }

  if (companyCode === 'selfsignup') {
    const initialOptions = await makeRequest(Api.signup.initialOptions).catch(err => console.log(err));
    await initI18next(defaultLanguage, true);
    root.render((
      <Router history={history}>
        <Suspense fallback={null}>
          <DefaultQueryClientProvider>
            <SignUp initialOptions={initialOptions} />
            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          </DefaultQueryClientProvider>
        </Suspense>
      </Router>
    ));
    const loading = document.getElementById('loading');
    loading.parentNode.removeChild(loading);
    throw new Error('signup');
  }

  if (!companyCode || companyCode in topRoutesList) {
    // if root or no company code, but uri seems to be valid —
    // checking for logged-in state through systemUser request, alongside getting default companyCode
    const systemUser = await makeRequest(Api.users.me)
      .catch(catchRequestError('users/me'));

    // The main company is just the first company the user was created in, so it can be a disabled one while
    // others are active.
    const { userAccounts, mainCompanyId } = systemUser;
    const isActive = ({ company }) => !company.disabled;
    const startingAccount = userAccounts.find(account => isActive(account) && account.company.id === mainCompanyId)
      || userAccounts.find(isActive)
      || userAccounts.find(({ company }) => company.id === mainCompanyId)
      || userAccounts[0];
    companyCode = startingAccount.company.code;

    redirect('/' + companyCode + window.location.pathname);
  }
  // for most reloads/ direct navigation checking for logged-in state with initialOptions request
  const initialOptions = await makeRequest(Api.companies.$company.initialOptions, { company: companyCode })
    .catch(catchRequestError('initialOptions', companyCode));

  const locale = initialOptions.globalJson.locale;
  window.tibetVersion = initialOptions.globalJson.version;
  await initI18next(locale);

  return Promise.resolve(initialOptions);
})().then((initialOptions) => {

  root.render((
    <Router history={history}>
      <Suspense fallback={null}>
        <QueryClientProvider client={queryClient}>
          <Layout initialOptions={initialOptions} />
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
        </QueryClientProvider>
      </Suspense>
    </Router>
  ));
}).catch((e) => {
  if (!['public', 'signup', 'companyDisabled'].includes(e.message)) {
    console.error('Unhandled error response from', e.ns, e.message, e.statusCode, e.stack);
  }
});
